import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import toast from "react-hot-toast";
import FileUpload from "../components/FileUpload";
import GuideForm from "../components/GuideForm";
import LoadingSpinner from "../components/LoadingSpinner";
import PromoCodeInput from "../components/PromoCodeInput";
import Footer from "../components/Footer";

import API_BASE from "../config/api";
import Navbar from "../components/Navbar";
import {
  ACCOUNT_LABEL,
  buildBoldChoicesUrl,
} from "../utils/ecosystemLinks";
import { withApiCredentials } from "../utils/apiAuth";
import "../styles/shared.css";

const PREP101_STRIPE_LINKS = {
  singleGuide: "https://buy.stripe.com/6oU3cv8Cb2jlbYN0tQ2wU3Z",
  threePack: "https://buy.stripe.com/7sYaEX4lV9LN3sh90m2wV0d",
};
const PREP101_MONTHLY_GUIDE_LIMIT = 5;

// Simple progress bar
const ProgressBar = ({ value, max }) => {
  const pct = Math.max(0, Math.min(100, max ? (value / max) * 100 : 0));
  return (
    <div
      style={{
        width: "100%",
        height: 10,
        background: "#e5e7eb",
        borderRadius: 999,
      }}
    >
      <div
        style={{
          width: `${pct}%`,
          height: "100%",
          background: "var(--gold)",
          borderRadius: 999,
        }}
      />
    </div>
  );
};

const SectionHeader = ({ eyebrow, title, description, align = "left" }) => (
  <div
    style={{
      textAlign: align,
      marginBottom: "1.25rem",
    }}
  >
    {eyebrow && (
      <div
        style={{
          fontSize: 12,
          fontWeight: 900,
          letterSpacing: "0.12em",
          textTransform: "uppercase",
          color: "#8b5e3c",
          marginBottom: 8,
        }}
      >
        {eyebrow}
      </div>
    )}
    <h2
      style={{
        color: "#0f172a",
        fontSize: "clamp(1.35rem, 2.2vw, 2rem)",
        lineHeight: 1.1,
        margin: 0,
      }}
    >
      {title}
    </h2>
    {description && (
      <p
        style={{
          color: "#475569",
          fontSize: "1rem",
          lineHeight: 1.7,
          margin: "0.65rem 0 0",
        }}
      >
        {description}
      </p>
    )}
  </div>
);

const StepHint = ({ number, title, description }) => (
  <div
    style={{
      display: "flex",
      gap: 12,
      alignItems: "flex-start",
      padding: "0.95rem",
      borderRadius: 16,
      background: "#f8fafc",
      border: "1px solid #e2e8f0",
    }}
  >
    <div
      style={{
        width: 28,
        height: 28,
        borderRadius: "50%",
        background: "#0f172a",
        color: "#fff",
        display: "grid",
        placeItems: "center",
        fontWeight: 900,
        flex: "0 0 auto",
        fontSize: 13,
      }}
    >
      {number}
    </div>
    <div>
      <div style={{ color: "#0f172a", fontWeight: 800, marginBottom: 4 }}>
        {title}
      </div>
      <div style={{ color: "#64748b", lineHeight: 1.5, fontSize: 14 }}>
        {description}
      </div>
    </div>
  </div>
);

const nicePlan = (p) => {
  switch ((p || "").toLowerCase()) {
    case "free":
      return "Free";
    case "basic":
      return "Starter";
    case "premium":
      return "Premium";
    case "starter":
      return "Starter";
    case "alacarte":
      return "A la carte";
    case "unlimited":
      return "Unlimited";
    case "bundle":
      return "Bundle";
    case "reader101_monthly":
      return "Reader101 Monthly";
    case "boldchoices_monthly":
      return "Bold Choices Monthly";
    default:
      return "—";
  }
};

const DASHBOARD_CONTEXT = {
  all: {
    contextLabel: "Account Hub",
    focusLine: "Choose a product below to get started.",
    title: `Your ${ACCOUNT_LABEL}`,
    subtitle: "Manage Prep101, Reader101, and Bold Choices in one place.",
  },
  prep101: {
    contextLabel: "Prep101",
    focusLine: "Build a full audition guide from uploaded sides.",
    title: `Your ${ACCOUNT_LABEL}`,
    subtitle: "Manage Prep101, Reader101, and Bold Choices in one place.",
  },
  reader101: {
    contextLabel: "Reader101",
    focusLine: "Continue your Reader101 work. You're logged in and ready to go.",
    title: `Your ${ACCOUNT_LABEL}`,
    subtitle: "Manage Prep101, Reader101, and Bold Choices in one place.",
  },
  bold_choices: {
    contextLabel: "Bold Choices",
    focusLine: "Continue your actor-first choice work.",
    title: `Your ${ACCOUNT_LABEL}`,
    subtitle: "Manage Prep101, Reader101, and Bold Choices in one place.",
  },
};

const getProductContext = (key) =>
  DASHBOARD_CONTEXT[key] || DASHBOARD_CONTEXT.all;

const formatPrepAccess = (usage) => {
  if (!usage) return "Checking access...";
  return `${PREP101_MONTHLY_GUIDE_LIMIT} guides/month`;
};

const formatPrepDetails = (usage) => {
  if (!usage) return "Add more anytime";
  const pieces = ["Add more anytime"];
  if (Number(usage.topUpCredits || 0) > 0) {
    pieces.push(
      `${usage.topUpCredits} top-up ${
        usage.topUpCredits === 1 ? "credit" : "credits"
      }`
    );
  }
  return pieces.join(" • ");
};

const formatPrepUsageCount = (usage) => {
  const used = Math.max(0, Number(usage?.monthlyUsed ?? usage?.used ?? 0));
  const displayUsed = Math.min(used, PREP101_MONTHLY_GUIDE_LIMIT);
  return `${displayUsed} of ${PREP101_MONTHLY_GUIDE_LIMIT} guides used`;
};

const formatReaderAccess = (usage) => {
  if (!usage) return "Checking access...";
  if (usage.unlimited) return "Unlimited access";
  if (usage.credits > 0) {
    return `${usage.credits} Reader101 ${
      usage.credits === 1 ? "credit" : "credits"
    }`;
  }
  return "No Reader101 credits yet";
};

const formatBoldAccess = (usage) => {
  if (!usage) return "Checking access...";
  if (usage.unlimited) return "Included with your plan";
  if (usage.credits > 0) {
    return `${usage.credits} Bold Choices ${
      usage.credits === 1 ? "credit" : "credits"
    }`;
  }
  return "Included access";
};

const Dashboard = () => {
  // Always start with no upload state on page load — sessionStorage is cleared immediately
  // so a hard reload never shows a ghost "file uploaded" state.
  const [uploadData, setUploadData] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isUploadingPdf, setIsUploadingPdf] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [activeGenerationMode, setActiveGenerationMode] = useState("standard");
  const [refreshKey, setRefreshKey] = useState(0);

  // usage state
  const [usage, setUsage] = useState(null);
  const [usageLoading, setUsageLoading] = useState(true);
  const [usageError, setUsageError] = useState(null);

  const { user, refreshUser } = useAuth();
  const token = (
    user?.accessToken ||
    user?.token ||
    // Fallback: after a page refresh, /verify returns a serialized user without the token
    // embedded. AuthContext tries localStorage (ca101_token) but may not have merged it back
    // into the user object. Read it directly here as a safety net.
    (() => { try { return localStorage.getItem('ca101_token') || null; } catch (_) { return null; } })()
  );
  const activeActor = user?.account?.activeActor;
  const onboardingRequired = Boolean(user?.account?.onboardingRequired);
  const needsActorSelection = Boolean(user?.account?.needsActorSelection);

  // Clear any stale cached upload data on mount — hard reloads must always start clean.
  useEffect(() => {
    try {
      sessionStorage.removeItem("prep101_upload_data");
    } catch (_) {}
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const product = params.get("product");
    if (product === "reader101" || product === "bold_choices" || product === "prep101") {
      setGuideFilter(product);
    }
    if (window.location.hash === "#guide-builder") {
      window.setTimeout(() => {
        document
          .getElementById("guide-builder")
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 250);
    }
  }, []);

  // ====== GUIDES LIBRARY ======
  const [guides, setGuides] = useState([]);
  const [guidesLoading, setGuidesLoading] = useState(false);
  const [guideFilter, setGuideFilter] = useState('all');
  const [playbookItems, setPlaybookItems] = useState([]);
  const [playbookLoading, setPlaybookLoading] = useState(false);

  // ====== USAGE FETCH ======
  useEffect(() => {
    let cancelled = false;

    const fetchUsage = async () => {
      setUsageLoading(true);
      setUsageError(null);

      try {
        const res = await fetch(
          `${API_BASE}/api/auth/dashboard`,
          withApiCredentials({}, user)
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const json = await res.json();
        console.log("🔍 Dashboard API response:", json);
        if (!cancelled) {
          const prep101Usage = json.user?.prep101Usage || {};
          const transformedUsage = {
            plan:
              prep101Usage.plan ||
              json.user?.subscription ||
              json.subscription?.currentPlan?.name,
            used: prep101Usage.monthlyUsed ?? json.user?.guidesUsed ?? 0,
            limit: prep101Usage.monthlyLimit ?? json.user?.guidesLimit ?? 0,
            remaining: prep101Usage.totalRemaining,
            monthlyRemaining: prep101Usage.monthlyRemaining,
            topUpCredits:
              prep101Usage.topUpCredits ?? json.user?.prep101TopUpCredits ?? 0,
            renewsAt:
              prep101Usage.renewsAt ||
              json.subscription?.renewsAt ||
              new Date(Date.now() + 1000 * 60 * 60 * 24 * 30).toISOString(),
            prep101: prep101Usage,
            reader101: json.user?.reader101Usage || {
              unlimited: false,
              credits: json.user?.reader101Credits || 0,
              canGenerate: (json.user?.reader101Credits || 0) > 0,
            },
            boldChoices: json.user?.boldChoicesUsage || {
              unlimited: false,
              credits: json.user?.boldChoicesCredits || 0,
              canGenerate: (json.user?.boldChoicesCredits || 0) > 0,
            },
          };
          console.log("🔍 Transformed usage data:", transformedUsage);
          setUsage(transformedUsage);
        }
      } catch (err) {
        // Fallback mock so the UI still works in dev
        if (!cancelled) {
          setUsage({
            plan: user?.subscription || "free",
            used: user?.guidesUsed || 0,
            limit: user?.guidesLimit ?? 0,
            remaining: user?.prep101Usage?.totalRemaining,
            monthlyRemaining: user?.prep101Usage?.monthlyRemaining,
            topUpCredits:
              user?.prep101Usage?.topUpCredits ||
              user?.prep101TopUpCredits ||
              0,
            prep101: user?.prep101Usage,
            reader101: user?.reader101Usage || {
              unlimited: false,
              credits: user?.reader101Credits || 0,
              canGenerate: (user?.reader101Credits || 0) > 0,
            },
            boldChoices: user?.boldChoicesUsage || {
              unlimited: false,
              credits: user?.boldChoicesCredits || 0,
              canGenerate: (user?.boldChoicesCredits || 0) > 0,
            },
            renewsAt: new Date(
              Date.now() + 1000 * 60 * 60 * 24 * 7
            ).toISOString(), // +7 days
          });
          setUsageError("Using fallback data until API is ready.");
        }
      } finally {
        if (!cancelled) setUsageLoading(false);
      }
    };

    fetchUsage();
    return () => { cancelled = true; };
  }, [user, refreshKey]);

  // Fetch guide library
  useEffect(() => {
    if (!user) return;
    setGuidesLoading(true);
    fetch(`${API_BASE}/api/guides`, {
      ...withApiCredentials({}, user)
    })
      .then(r => r.ok ? r.json() : { guides: [] })
      .then(data => setGuides(data.guides || []))
      .catch(() => {})
      .finally(() => setGuidesLoading(false));
  }, [user, refreshKey]);

  // Fetch Bold Choices playbook saves
  useEffect(() => {
    if (!user) return;
    setPlaybookLoading(true);
    fetch(`${API_BASE}/api/bold-choices/saved`, withApiCredentials({}, user))
      .then((r) => (r.ok ? r.json() : { items: [] }))
      .then((data) => setPlaybookItems(Array.isArray(data.items) ? data.items : []))
      .catch(() => setPlaybookItems([]))
      .finally(() => setPlaybookLoading(false));
  }, [user, refreshKey]);

  const remaining = useMemo(() => {
    if (!usage) return 0;
    const prepUsage = usage.prep101 || {};
    const monthlyUsed = Number(prepUsage.monthlyUsed ?? usage.used ?? 0);
    const monthlyRemaining =
      typeof prepUsage.monthlyRemaining === "number"
        ? prepUsage.monthlyRemaining
        : Math.max(0, PREP101_MONTHLY_GUIDE_LIMIT - monthlyUsed);
    const topUpCredits = Number(prepUsage.topUpCredits ?? usage.topUpCredits ?? 0);
    return (
      Math.min(Math.max(0, monthlyRemaining), PREP101_MONTHLY_GUIDE_LIMIT) +
      Math.max(0, topUpCredits)
    );
  }, [usage]);

  const canGenerate = useMemo(() => {
    if (!usage) return false;
    return remaining > 0;
  }, [usage, remaining]);
  const isPrep101StarterPlan = useMemo(() => {
    const plan = String(usage?.plan || user?.subscription || "").toLowerCase();
    return plan === "starter" || plan === "basic";
  }, [usage?.plan, user?.subscription]);
  const dashboardContext = useMemo(
    () =>
      getProductContext(
        ["prep101", "reader101", "bold_choices"].includes(guideFilter)
          ? guideFilter
          : "all"
      ),
    [guideFilter]
  );
  const scrollToGuideBuilder = (product = "prep101") => {
    setGuideFilter(product);
    window.history.replaceState(
      {},
      "",
      `/dashboard?product=${encodeURIComponent(product)}`
    );
    document
      .getElementById("guide-builder")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const refreshDashboardAccess = async () => {
    setRefreshKey((k) => k + 1);
    if (typeof refreshUser === "function") {
      try {
        await refreshUser();
      } catch (error) {
        console.warn("Unable to refresh user after access update:", error);
      }
    }
  };
  const buildGuideDownloadUrl = (path) => {
    const baseUrl = `${API_BASE}${path}`;
    if (!token) return baseUrl;
    const sep = baseUrl.includes("?") ? "&" : "?";
    return `${baseUrl}${sep}token=${encodeURIComponent(token)}`;
  };

  // ====== FILE UPLOAD ======
  const handleUploadStart = (file) => {
    setIsUploadingPdf(true);
    setUploadedFile(file || null);
    setUploadData(null);
    try {
      sessionStorage.removeItem("prep101_upload_data");
    } catch (error) {
      console.warn("Could not clear cached upload data:", error);
    }
  };

  const handleUploadEnd = () => {
    setIsUploadingPdf(false);
  };

  const handleFileUpload = (data) => {
    const { localFile, ...serializableData } = data || {};
    setUploadedFile(localFile || null);
    setUploadData(serializableData);
    const extractedWords = Number(serializableData?.wordCount || 0);
    const uploadIsUsable =
      serializableData?.scriptReadable !== false && extractedWords > 0;
    const isReaderContext = guideFilter === "reader101";
    const canUseDirectPdf = !isReaderContext && Boolean(localFile);
    try {
      if (uploadIsUsable) {
        sessionStorage.setItem(
          "prep101_upload_data",
          JSON.stringify(serializableData)
        );
      } else {
        sessionStorage.removeItem("prep101_upload_data");
      }
    } catch (error) {
      console.warn("Could not cache upload data for recovery:", error);
    }
    if (serializableData?.scriptReadable === false) {
      // PDF uploaded but needs enhanced reading — always show a calm reassurance,
      // never an alarming error. Generation will attempt a deep-read pass automatically.
      toast(
        "PDF uploaded! This script uses a format that needs a deeper read — we'll handle that automatically when you generate.",
        { icon: "📄", duration: 6000 }
      );
      return;
    }
    if (extractedWords === 0) {
      // Truly empty — no text at all, no uploadId fallback possible
      toast(
        "No readable text found in this PDF. Try re-uploading or paste the sides text directly below.",
        { icon: "⚠️", duration: 6000 }
      );
      return;
    }
    if (serializableData.uploadMessage) {
      toast(serializableData.uploadMessage, { icon: "📄", duration: 5000 });
      return;
    }
    toast.success(`PDF uploaded — ${extractedWords} words extracted and ready.`);
  };

  // Open HTML in a new tab (Blob URL). Optionally reuse a pre-opened window.
  const openHtmlInNewTab = (htmlString, customTitle) => {
    const blob = new Blob([htmlString], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    // If customTitle is provided, it's a string for the window name
    if (typeof customTitle === "string") {
      const w = window.open(url, customTitle, "noopener,noreferrer");
      if (!w)
        toast('Popup blocked. Use the "Open last guide" link below.', {
          icon: "⚠️",
        });
    } else {
      const w = window.open(url, "_blank", "noopener,noreferrer");
      if (!w)
        toast('Popup blocked. Use the "Open last guide" link below.', {
          icon: "⚠️",
        });
    }
  };

  // ====== GENERATE GUIDE ======
  const handleGenerateGuide = async (formData) => {
    let payload = null;
    let loadingToastId = null;
    const effectiveFormData = {
      ...(formData || {}),
      mode: isReader101Context ? "reader_support" : (formData?.mode || "standard"),
      product: isReader101Context ? "reader101" : "prep101",
      isReader101: isReader101Context,
    };
    const modeForRequest = effectiveFormData.mode;
    setActiveGenerationMode(modeForRequest);
    const normalizedUploadIdsForDirectPdf = (
      uploadData?.uploadIds || [uploadData?.uploadId]
    ).filter(Boolean);
    const uploadLooksUsable =
      uploadData?.scriptReadable !== false &&
      !uploadData?.fallbackMode &&
      Number(uploadData?.wordCount || 0) > 0 &&
      Boolean(uploadData?.sceneText || uploadData?.text);
    // Do not re-read PDFs once upload has recovered usable script text. That
    // path is slower, can time out on Vercel, and can discard the OCR result.
    // Only use generate-from-pdf when upload is partial/unreadable and we need
    // the backend to try one more PDF-native recovery pass.
    const shouldUseTwoCallPdfEndpoint =
      !uploadLooksUsable &&
      (Boolean(uploadedFile) ||
        (isReader101Context && normalizedUploadIdsForDirectPdf.length > 0));

    // Generation endpoints go DIRECT to Vercel — never through the Netlify proxy.
    // Netlify's proxy (prep101.childactor101.com → Vercel) has a hard 26-second timeout;
    // guide generation takes 3–6 minutes, so proxied calls always 504.
    // Non-generation calls (upload, auth, usage) are short and stay on API_BASE.
    const GENERATION_API_BASE = API_BASE || 'https://prep101-api.vercel.app';

    if (!uploadData?.uploadId && !uploadData?.uploadIds) {
      toast.error("Please upload your sides (PDF) before generating.");
      return;
    }
    if (uploadData?.scriptReadable === false && !shouldUseTwoCallPdfEndpoint) {
      // If we can't use the two-call PDF endpoint AND the upload wasn't readable,
      // the user needs to try a different approach. Show a calm, helpful prompt.
      toast(
        "This PDF needs a deeper read, but we don't have the original file to retry. Please re-upload the PDF or paste the sides text directly below.",
        { icon: "📄", duration: 7000 }
      );
      return;
    }
    if (modeForRequest !== "reader_support" && !canGenerate) {
      toast.error(
        isPrep101StarterPlan
          ? "You've used your 5 Prep101 guides. Buy 1 more guide or a 3-pack to keep going."
          : "You've hit your guide limit. Visit pricing for more options."
      );
      return;
    }

    setIsGenerating(true);

    // Add timeout to prevent infinite hanging
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 600000); // 10 minute timeout

    const buildMultipartPayload = () => {
      const multipart = new FormData();
      if (uploadedFile) {
        multipart.append("file", uploadedFile);
      }
      Object.entries(effectiveFormData || {}).forEach(([key, value]) => {
        if (value == null) return;
        multipart.append(key, String(value));
      });
      const uploadIds = (
        uploadData?.uploadIds || [uploadData?.uploadId]
      ).filter(Boolean);
      // Always send the primary uploadId for backward compat
      if (uploadIds[0]) {
        multipart.append("uploadId", String(uploadIds[0]));
      }
      // Also send all uploadIds so the backend can merge multiple PDFs
      uploadIds.forEach((id) => multipart.append("uploadIds[]", String(id)));
      // Tell the backend which guide type to generate
      if (modeForRequest) {
        multipart.append("mode", modeForRequest);
      }
      return multipart;
    };

    const parseAndHandleGuideResponse = async (response) => {
      const ct = response.headers.get("content-type") || "";
      let parsedData;

      if (!response.ok && !ct.includes("application/json")) {
        const rawBody = await response.text().catch(() => "");
        const preview = rawBody.slice(0, 200).trim();
        const timeoutMarker = /FUNCTION_INVOCATION_TIMEOUT/i.test(rawBody);
        const isHtmlError = preview.toLowerCase().startsWith("<!doctype") || preview.toLowerCase().startsWith("<html");
        const errorDetail = timeoutMarker
          ? "Guide generation timed out on the server (FUNCTION_INVOCATION_TIMEOUT)."
          : isHtmlError
            ? `Server returned HTML (HTTP ${response.status}) — likely a proxy or edge error.`
            : (preview || `Request failed (HTTP ${response.status})`);
        console.error("❌ Non-JSON API error:", response.status, errorDetail);
        throw new Error(errorDetail);
      }

      if (ct.includes("application/json")) {
        parsedData = await response.json();
        console.log("🎭 Guide generation response:", parsedData);

        // Async Job Handling
        if (response.status === 202 && parsedData.jobId) {
          let jobStatus = "waiting";
          let jobData = null;
          
          while (jobStatus !== "completed" && jobStatus !== "failed") {
            await new Promise(resolve => setTimeout(resolve, 3000));
            
            const pollRes = await fetch(`${GENERATION_API_BASE}/api/guides/jobs/${parsedData.jobId}`, {
              cache: "no-store",
              headers: token ? { Authorization: `Bearer ${token}` } : {},
            });
            
            if (!pollRes.ok) {
              const errorPayload = await pollRes.json().catch(() => null);
              throw new Error(
                errorPayload?.reason ||
                  errorPayload?.error ||
                  "Failed to poll guide status"
              );
            }
            jobData = await pollRes.json();
            jobStatus = jobData.state;
            
            if (jobData.statusMessage && loadingToastId) {
              toast.loading(`${jobData.progress}% - ${jobData.statusMessage}`, { id: loadingToastId });
            }
          }
          
          if (jobStatus === "failed") {
            throw new Error(jobData.failedReason || "Guide generation failed in background worker.");
          }
          
          // Use the finalized job data
          parsedData = jobData;
        }

        if (parsedData?.guideContent) {
          openHtmlInNewTab(parsedData.guideContent);

          if (parsedData.savedToDatabase === false) {
            toast(
              "Guide created but not saved to your account. Please log in to save guides.",
              { icon: "⚠️" }
            );
          }

          if (parsedData.childGuideMessage) {
            toast(parsedData.childGuideMessage, { icon: "🌟" });
          } else if (parsedData.childGuideQueued) {
            toast(
              "Child guide is being generated in the background and will appear on your dashboard shortly.",
              { icon: "🌟" }
            );
          }
          return parsedData;
        }

        if (!response.ok || parsedData?.success === false) {
          const serverMessage = [
            parsedData?.error,
            parsedData?.reason,
            parsedData?.detail,
            parsedData?.message,
            parsedData?.debug?.tip,
          ].filter(Boolean).join(" ");
            
          const err = new Error(
            serverMessage || `Failed to generate guide (HTTP ${response.status})`
          );
          if (parsedData?.step) err.step = parsedData.step;
          throw err;
        }

        throw new Error("No guide content returned.");
      }

      const html = await response.text();
      if (!html || html.length < 50) {
        throw new Error("Empty guide response.");
      }
      openHtmlInNewTab(html);
      return parsedData;
    };

    try {
      const normalizedUploadIds = (
        uploadData.uploadIds || [uploadData.uploadId]
      ).filter(Boolean);
      const normalizedSceneText = uploadData.sceneText || uploadData.text || "";
      const normalizedFilename =
        uploadData.filename || uploadData.originalName || "uploaded-sides.pdf";
      const normalizedScenePayloads =
        uploadData.scenePayloads ||
        (normalizedUploadIds[0] && normalizedSceneText
          ? {
              [normalizedUploadIds[0]]: {
                filename: normalizedFilename,
                sceneText: normalizedSceneText,
                characterNames: uploadData.characterNames || [],
                extractionMethod: uploadData.extractionMethod || "upload",
                extractionConfidence:
                  uploadData.extractionConfidence || "unknown",
                wordCount: uploadData.wordCount || 0,
                pageCount: uploadData.pageCount || null,
                fileType: uploadData.fileType || "sides",
                fallbackMode: Boolean(uploadData.fallbackMode),
                warnings: uploadData.warnings || [],
                source: uploadData.source || uploadData.extractionMethod || "text",
              },
            }
          : {});

      payload = {
        uploadId: uploadData.uploadId || normalizedUploadIds[0],
        uploadIds: normalizedUploadIds,
        scenePayloads: normalizedScenePayloads,
        // Fallback data for stateless serverless environments
        sceneText: normalizedSceneText,
        filename: normalizedFilename,
        wordCount: uploadData.wordCount,
        pageCount: uploadData.pageCount,
        characterNames: uploadData.characterNames,
        fallbackMode: uploadData.fallbackMode || false,
        warnings: uploadData.warnings || [],
        source: uploadData.source || uploadData.extractionMethod || "text",
        mode: modeForRequest,
        ...effectiveFormData,
      };
      console.log("🚀 Starting guide generation for:", effectiveFormData.characterName);
      const loadingCopy =
        modeForRequest === "reader_support"
          ? "Generating your Reader101 guide... this may take about 3-6 minutes."
          : "Generating your Prep101 guide (extract -> analyze -> summarize -> final HTML)... this may take about 3-6 minutes.";
      loadingToastId = toast.loading(loadingCopy);
      let res;
      const runFetchWithRetry = async (url, init, retryInit = null) => {
        try {
          return await fetch(url, init);
        } catch (error) {
          const isNetworkFetchError =
            error instanceof TypeError &&
            /failed to fetch/i.test(String(error?.message || ""));
          if (!isNetworkFetchError || !retryInit) {
            throw error;
          }
          console.warn("Primary fetch failed, retrying with fallback request profile...", error);
          await new Promise((resolve) => setTimeout(resolve, 400));
          return fetch(url, retryInit);
        }
      };
      if (shouldUseTwoCallPdfEndpoint) {
        const multipart = buildMultipartPayload();
        const multipartHeaders = token ? { Authorization: `Bearer ${token}` } : {};
        const primaryInit = {
          method: "POST",
          ...withApiCredentials({ headers: multipartHeaders }, user),
          body: multipart,
          signal: controller.signal,
        };
        const retryInit = {
          method: "POST",
          headers: multipartHeaders,
          body: multipart,
          signal: controller.signal,
          mode: "cors",
          credentials: "omit",
        };
        res = await runFetchWithRetry(
          `${GENERATION_API_BASE}/api/guides/generate-from-pdf`,
          primaryInit,
          retryInit
        );
      } else {
        const headers = {
          "Content-Type": "application/json",
        };
        const primaryInit = {
          method: "POST",
          ...withApiCredentials({ headers }, user),
          body: JSON.stringify(payload),
          signal: controller.signal,
        };
        const retryHeaders = token
          ? { ...headers, Authorization: `Bearer ${token}` }
          : headers;
        const retryInit = {
          method: "POST",
          headers: retryHeaders,
          body: JSON.stringify(payload),
          signal: controller.signal,
          mode: "cors",
          credentials: "omit",
        };
        res = await runFetchWithRetry(
          `${GENERATION_API_BASE}/api/guides/generate`,
          primaryInit,
          retryInit
        );
      }

      clearTimeout(timeoutId);
      const data = await parseAndHandleGuideResponse(res);

      const successCopy =
        modeForRequest === "reader_support"
          ? "Reader101 guide generated successfully! Opening now..."
          : "Prep101 guide generated successfully! Opening now...";
      toast.success(successCopy);
      try {
        sessionStorage.setItem("prep101_upload_data", JSON.stringify(uploadData));
      } catch (error) {
        console.warn("Could not refresh cached upload data after guide generation:", error);
      }
      setRefreshKey((k) => k + 1);

      if (data?.prep101Usage) {
        setUsage((u) => ({
          ...u,
          plan: data.prep101Usage.plan || u?.plan,
          used: data.prep101Usage.monthlyUsed ?? u?.used ?? 0,
          limit: data.prep101Usage.monthlyLimit ?? u?.limit ?? null,
          remaining: data.prep101Usage.totalRemaining,
          monthlyRemaining: data.prep101Usage.monthlyRemaining,
          topUpCredits: data.prep101Usage.topUpCredits ?? u?.topUpCredits ?? 0,
          renewsAt: data.prep101Usage.renewsAt || u?.renewsAt,
          prep101: data.prep101Usage,
        }));
      } else if (usage?.limit != null) {
        setUsage((u) => ({
          ...u,
          used: (u?.used || 0) + 1,
          remaining:
            typeof u?.remaining === "number"
              ? Math.max(0, u.remaining - 1)
              : u?.remaining,
          prep101: u?.prep101
            ? {
                ...u.prep101,
                monthlyUsed: (u.prep101.monthlyUsed || 0) + 1,
                monthlyRemaining:
                  typeof u.prep101.monthlyRemaining === "number"
                    ? Math.max(0, u.prep101.monthlyRemaining - 1)
                    : u.prep101.monthlyRemaining,
                totalRemaining:
                  typeof u.prep101.totalRemaining === "number"
                    ? Math.max(0, u.prep101.totalRemaining - 1)
                    : u.prep101.totalRemaining,
              }
            : u?.prep101,
        }));
      }
    } catch (err) {
      const errorMessage = String(err?.message || "");
      const isHardServerTimeout =
        /FUNCTION_INVOCATION_TIMEOUT|timed out on the server|HTTP 504/i.test(errorMessage);
      const canAttemptRecovery =
        !isHardServerTimeout &&
        /Empty guide response|Guide truncated|Missing Two-Take Strategy|Missing checklist|aborted a request|AbortError/i.test(
          errorMessage
        );

      if (canAttemptRecovery) {
        try {
          const directApiBase = "https://prep101-api.vercel.app";
          let recoveryResponse;

          if (shouldUseTwoCallPdfEndpoint) {
            recoveryResponse = await fetch(
              `${directApiBase}/api/guides/generate-from-pdf`,
              {
                method: "POST",
                headers: token ? { Authorization: `Bearer ${token}` } : {},
                body: buildMultipartPayload(),
                mode: "cors",
                credentials: "omit",
              }
            );
          } else {
            recoveryResponse = await fetch(`${directApiBase}/api/guides/generate`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
              },
              body: JSON.stringify(payload),
              mode: "cors",
              credentials: "omit",
            });
          }

          await parseAndHandleGuideResponse(recoveryResponse);
          toast.success("Guide recovered and opened.");
          return;
        } catch (recoveryError) {
          console.error("Guide recovery attempt failed:", recoveryError);
        }
      }
      console.error("Guide generation error:", err);

      if (
        err.name === "AbortError" ||
        /aborted a request|timeout|FUNCTION_INVOCATION_TIMEOUT|HTTP 504/i.test(errorMessage)
      ) {
        toast.error(
          isHardServerTimeout
            ? "Guide generation timed out on the server. We stopped this attempt instead of pretending it is still building. Please retry, or paste the recovered scene text if this PDF keeps timing out."
            : "The generation process timed out. This usually happens with very long scripts or during high traffic. Please try again or paste the scene text directly below.",
          { duration: 8000 }
        );
      } else if (/unable to recover recognizable screenplay sides|unable to read the uploaded sides|Unable to recover meaningful text/i.test(errorMessage)) {
        // All PDF reading fallbacks were exhausted — guide cannot be generated from this file
        toast(
          "We weren't able to read the script from this PDF after trying our enhanced reading mode. Please re-upload a clearer PDF, or paste the sides text directly in the field below.",
          { icon: "📄", duration: 9000 }
        );
        setUploadData(null);
        setUploadedFile(null);
        try { sessionStorage.removeItem("prep101_upload_data"); } catch (_) {}
      } else {
        toast.error(`Failed to generate guide: ${err.message}`, { duration: 8000 });
      }
    } finally {
      clearTimeout(timeoutId);
      setIsGenerating(false);
      if (loadingToastId) toast.dismiss(loadingToastId);
    }
  };

  const isReader101Context = guideFilter === "reader101";
  const extractedWords = Number(uploadData?.wordCount || 0);
  const uploadIsUsable =
    Boolean(uploadData) &&
    uploadData?.scriptReadable !== false &&
    extractedWords > 0;
  const uploadCanProceedViaDirectPdf =
    Boolean(uploadData) &&
    (Boolean(uploadedFile) ||
      (isReader101Context &&
        Boolean(uploadData?.uploadId || uploadData?.uploadIds?.[0])));
  const canShowGuideDetails = uploadIsUsable || uploadCanProceedViaDirectPdf;
  const uploadHasFailed = Boolean(uploadData) && !uploadIsUsable;

  // ====== RENDER ======
  const renewText = usage?.renewsAt
    ? new Date(usage.renewsAt).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
    })
    : null;
  const prepUsage = usage?.prep101 || user?.prep101Usage || {};
  const prepMonthlyUsed = Math.min(
    Math.max(0, Number(prepUsage.monthlyUsed ?? usage?.used ?? 0)),
    PREP101_MONTHLY_GUIDE_LIMIT
  );
  const prepTopUpCredits = Number(
    prepUsage.topUpCredits ?? usage?.topUpCredits ?? 0
  );
  const showPrepOveragePrompt =
    prepMonthlyUsed > 0 || prepMonthlyUsed >= PREP101_MONTHLY_GUIDE_LIMIT;
  const builderMode = isReader101Context ? "reader_support" : "standard";
  const builderCopy = isReader101Context
    ? {
        eyebrow: "Reader101 Builder",
        title: "Create a Reader101 guide",
        description:
          "Upload the sides, confirm the role details, and generate reader coaching for the person holding the lines.",
        steps: [
          ["1", "Upload sides", "Drop in the PDF for the self-tape scene."],
          ["2", "Choose Reader101", "The form will create a reader support guide, not an actor guide."],
          ["3", "Generate the guide", "Get pacing, energy, listening, and key-beat coaching for the reader."],
        ],
      }
    : {
        eyebrow: "Prep101 Builder",
        title: "Build a full audition guide",
        description:
          "Start by uploading your sides. After the PDF is processed, we'll ask for the role details and generate the guide.",
        steps: [
          ["1", "Upload sides", "Drop in the PDF for the audition scene."],
          ["2", "Confirm the role", "Tell us the character and production details."],
          ["3", "Generate the guide", "Get a Prep101 guide saved to this account."],
        ],
      };
  const prep101AccessBlocked =
    !isReader101Context && !usageLoading && !canGenerate;

  return (
    <>
      <Navbar />

      {/* Full-screen overlay while a guide is being generated */}
      {isGenerating && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15, 23, 42, 0.92)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 50,
          }}
        >
          <div
            style={{
              maxWidth: 560,
              padding: "2rem",
              textAlign: "center",
              color: "#e5e7eb",
            }}
          >
            <LoadingSpinner mode={activeGenerationMode} />
            <p
              style={{
                marginTop: "1.5rem",
                fontSize: "1rem",
                color: "#e5e7eb",
              }}
            >
              {activeGenerationMode === "reader_support"
                ? "Crafting your Reader101 guide. This usually takes about 3–6 minutes."
                : "Crafting your Prep101 audition guide in staged passes (extract, analysis, summary, final HTML). This usually takes about 3–6 minutes."}
            </p>
            <p
              style={{
                marginTop: "0.5rem",
                fontSize: "0.875rem",
                color: "#9ca3af",
              }}
            >
              We also run completion checks before delivery so your final sections
              (Two-Take Strategy, checklist, Final Coach Note) are present. Please don&apos;t close the page while we work.
            </p>
          </div>
        </div>
      )}

      <div className="page-dark">
        <div className="container-wide">
          {/* Header */}
          <div className="page-hero">
            <img
              src="/preplogo.png"
              alt="Prep101 logo"
              className="logo-hero"
              loading="lazy"
            />
            <div
              style={{
                fontSize: 12,
                fontWeight: 800,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                color: "#8b5e3c",
                marginBottom: 10,
              }}
            >
              Currently viewing: {dashboardContext.contextLabel}
            </div>
            <h1 className="h1-hero">{dashboardContext.title}</h1>
            <p className="h2-hero">{dashboardContext.subtitle}</p>
            <p
              style={{
                color: "#cbd5e1",
                fontSize: "1.05rem",
                margin: "0.85rem auto 0",
                maxWidth: 680,
              }}
            >
              {dashboardContext.focusLine}
            </p>
            {activeActor && (
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                marginTop: 14,
                padding: '10px 16px',
                borderRadius: 999,
                background: 'rgba(255,255,255,0.12)',
                border: '1px solid rgba(255,255,255,0.18)',
                color: '#fff',
                fontWeight: 900,
              }}>
                🎭 Working as: {activeActor.actorName}
                {activeActor.ageRange ? ` (${activeActor.ageRange})` : ''}
              </div>
            )}
          </div>

          {onboardingRequired && (
            <div className="card-white" style={{ marginBottom: '1.5rem', border: '1px solid #f59e0b' }}>
              <div style={{ fontWeight: 800, color: '#92400e', marginBottom: 6 }}>
                Finish setting up your Child Actor 101 account
              </div>
              <div style={{ color: '#78350f', marginBottom: 12 }}>
                Choose whether you are here as an actor, a parent, or both, then set your active actor context.
              </div>
              <button
                className="btn btnPrimary"
                onClick={() => (window.location.href = '/onboarding')}
              >
                Continue Setup
              </button>
            </div>
          )}

          {!onboardingRequired && needsActorSelection && (
            <div className="card-white" style={{ marginBottom: '1.5rem', border: '1px solid #0f172a' }}>
              <div style={{ fontWeight: 800, color: '#0f172a', marginBottom: 6 }}>
                Choose the actor this work is for
              </div>
              <div style={{ color: '#475569', marginBottom: 12 }}>
                Your account has actor profiles, but none is selected as the active context yet.
              </div>
              <button
                className="btn btnPrimary"
                onClick={() => (window.location.href = '/select-actor')}
              >
                Choose Active Actor
              </button>
            </div>
          )}

          <div className="card-white">
            <SectionHeader
              eyebrow="Start Here"
              title="What would you like to work on?"
              description={`Use this shared account hub to choose a product, confirm ${activeActor?.actorName || "the active actor"}, and keep your guides in one place.`}
            />
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))",
                gap: "1rem",
              }}
            >
              {[
                {
                  label: "Prep101",
                  color: "#f59e0b",
                  description: "Upload sides here when you want a full audition guide.",
                  status: formatPrepAccess(usage?.prep101 || user?.prep101Usage || null),
                  secondaryStatus: formatPrepDetails(usage?.prep101 || user?.prep101Usage || null),
                  action: () => scrollToGuideBuilder("prep101"),
                  cta: "Build a Prep101 Guide",
                },
                {
                  label: "Reader101",
                  color: "#14b8a6",
                  description: "Create reader notes for the person holding the sides during the self-tape.",
                  status: formatReaderAccess(usage?.reader101 || user?.reader101Usage || null),
                  action: () => {
                    scrollToGuideBuilder("reader101");
                  },
                  cta: "Create a Reader101 Guide",
                },
                {
                  label: "Bold Choices",
                  color: "#FF4D4D",
                  description: "Open actor-first choice work for sharper, more specific decisions.",
                  status: formatBoldAccess(usage?.boldChoices || user?.boldChoicesUsage || null),
                  action: () => {
                    window.location.href = buildBoldChoicesUrl({ token, useBridge: Boolean(user) });
                  },
                  cta: "Open Bold Choices",
                },
              ].map((workspace) => (
                <div
                  key={workspace.label}
                  style={{
                    border: `1px solid ${workspace.color}33`,
                    borderRadius: 18,
                    padding: "1.1rem",
                    background: `${workspace.color}10`,
                    display: "flex",
                    flexDirection: "column",
                    minHeight: 220,
                  }}
                >
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 900,
                      letterSpacing: "0.1em",
                      textTransform: "uppercase",
                      color: workspace.color,
                      marginBottom: 8,
                    }}
                  >
                    {workspace.label}
                  </div>
                  <div
                    style={{
                      fontSize: 15,
                      color: "#334155",
                      lineHeight: 1.6,
                      marginBottom: 12,
                    }}
                  >
                    {workspace.description}
                  </div>
                  <div
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      fontSize: 12,
                      fontWeight: 800,
                      borderRadius: 999,
                      background: "#fff",
                      color: "#0f172a",
                      padding: "6px 10px",
                      margin: "auto 0 14px",
                      width: "fit-content",
                    }}
                  >
                    {workspace.status}
                  </div>
                  {workspace.secondaryStatus && (
                    <div
                      style={{
                        color: "#475569",
                        fontSize: 13,
                        fontWeight: 700,
                        margin: "-6px 0 14px",
                      }}
                    >
                      {workspace.secondaryStatus}
                    </div>
                  )}
                  {workspace.label === "Prep101" && showPrepOveragePrompt && (
                    <div
                      style={{
                        borderRadius: 14,
                        background: "#fff7ed",
                        border: "1px solid #fed7aa",
                        color: "#9a3412",
                        padding: "0.75rem",
                        marginBottom: 14,
                        fontSize: 13,
                        lineHeight: 1.45,
                      }}
                    >
                      <strong>{formatPrepUsageCount(prepUsage)}</strong>
                      <button
                        type="button"
                        onClick={() => (window.location.href = PREP101_STRIPE_LINKS.threePack)}
                        style={{
                          display: "block",
                          marginTop: 6,
                          border: "none",
                          background: "transparent",
                          color: "#c2410c",
                          fontWeight: 900,
                          padding: 0,
                          cursor: "pointer",
                          textDecoration: "underline",
                        }}
                      >
                        Need more? Add 3 more
                      </button>
                    </div>
                  )}
                  <button
                    onClick={workspace.action}
                    className="btn btnPrimary"
                    style={{
                      width: "100%",
                      background: workspace.color,
                      border: "none",
                      color: workspace.label === "Reader101" ? "#041311" : "#fff",
                    }}
                  >
                    {workspace.cta}
                  </button>
                </div>
              ))}
            </div>
            <div
              style={{
                marginTop: "1.25rem",
                padding: "1rem 1.1rem",
                borderRadius: 18,
                background: "#0f172a",
                color: "#e2e8f0",
                fontWeight: 800,
                textAlign: "center",
                lineHeight: 1.6,
              }}
            >
              Prep101 builds your performance. Reader101 supports your tape.
              Bold Choices sharpens your choices.
            </div>
          </div>

          {/* Usage strip */}
          <div className="card-white">
            <SectionHeader
              eyebrow="Your Access"
              title="What you can use right now"
              description="This is your current access across all three tools, including monthly access and any remaining credits."
            />
            {usageLoading ? (
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <LoadingSpinner />
                <span style={{ color: "#6b7280" }}>Loading plan…</span>
              </div>
            ) : (
              <>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "baseline",
                    marginBottom: 6,
                  }}
                >
                  <div style={{ fontWeight: 800, color: "#0f172a" }}>
                    Your Plan: {nicePlan(usage?.plan || user?.subscription)}
                    {usageError && (
                      <span style={{ color: "#f59e0b", marginLeft: 8 }}>
                        (demo)
                      </span>
                    )}
                  </div>
                  <div style={{ color: "#475569", fontWeight: 700 }}>
                    {prepMonthlyUsed} / {PREP101_MONTHLY_GUIDE_LIMIT} Prep101 used
                  </div>
                </div>
                {prepTopUpCredits > 0 && (
                  <div
                    style={{
                      color: "#0f766e",
                      fontWeight: 700,
                      marginBottom: 8,
                    }}
                  >
                    {prepTopUpCredits} Prep101 add-on credit
                    {prepTopUpCredits === 1 ? "" : "s"} remaining
                  </div>
                )}
                <ProgressBar
                  value={prepMonthlyUsed}
                  max={PREP101_MONTHLY_GUIDE_LIMIT}
                />
                <div
                  style={{ color: "#64748b", marginTop: 6, fontSize: 13 }}
                >
                  {remaining} Prep101 guide{remaining === 1 ? "" : "s"} available
                  {renewText ? ` • monthly guides renew ${renewText}` : ""}
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                    gap: 10,
                    marginTop: 14,
                  }}
                >
                  {[
                    {
                      label: "Prep101",
                      tone: "#f59e0b",
                      value: `${formatPrepAccess(usage?.prep101 || null)} • ${formatPrepDetails(usage?.prep101 || null)}`,
                    },
                    {
                      label: "Reader101",
                      tone: "#14b8a6",
                      value: formatReaderAccess(usage?.reader101 || null),
                    },
                    {
                      label: "Bold Choices",
                      tone: "#ef4444",
                      value: formatBoldAccess(usage?.boldChoices || null),
                    },
                  ].map((item) => (
                    <div
                      key={item.label}
                      style={{
                        border: `1px solid ${item.tone}33`,
                        borderRadius: 12,
                        padding: "0.8rem 0.9rem",
                        background: `${item.tone}10`,
                      }}
                    >
                      <div
                        style={{
                          fontSize: 11,
                          fontWeight: 800,
                          letterSpacing: "0.08em",
                          textTransform: "uppercase",
                          color: item.tone,
                          marginBottom: 6,
                        }}
                      >
                        {item.label}
                      </div>
                      <div
                        style={{
                          color: "#1e293b",
                          fontWeight: 700,
                          lineHeight: 1.5,
                        }}
                      >
                        {item.value}
                      </div>
                    </div>
                  ))}
                </div>
                {!canGenerate && (
                  <div
                    style={{
                      marginTop: 10,
                      background: "#fffbeb",
                      border: "1px solid #f59e0b",
                      borderRadius: 10,
                      color: "#92400e",
                      padding: "8px 10px",
                    }}
                  >
                    <div style={{ fontWeight: 700, marginBottom: 8 }}>
                      {isPrep101StarterPlan
                        ? "You've used your 5 Prep101 Starter guides."
                        : "You've hit your monthly limit."}
                    </div>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      {isPrep101StarterPlan ? (
                        <>
                          <button
                            onClick={() => (window.location.href = PREP101_STRIPE_LINKS.singleGuide)}
                            className="btn"
                            style={{
                              padding: "6px 10px",
                              fontSize: "0.875rem",
                              background: "#0f172a",
                              color: "#fff",
                              border: "none",
                            }}
                          >
                            Buy Single A La Carte — $11.99
                          </button>
                          <button
                            onClick={() => (window.location.href = PREP101_STRIPE_LINKS.threePack)}
                            className="btn btnPrimary"
                            style={{
                              padding: "6px 10px",
                              fontSize: "0.875rem",
                            }}
                          >
                            Buy 3-Pack — $21.99
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={() => (window.location.href = "/pricing")}
                          className="btn btnPrimary"
                          style={{
                            padding: "6px 10px",
                            fontSize: "0.875rem",
                          }}
                        >
                          See Plans
                        </button>
                      )}
                    </div>
                    <PromoCodeInput onRedeemSuccess={refreshDashboardAccess} />
                  </div>
                )}
                {canGenerate && (
                  <PromoCodeInput onRedeemSuccess={refreshDashboardAccess} />
                )}
              </>
            )}
          </div>

          {/* Body */}
          <div className="card-white" id="guide-builder">
            <SectionHeader
              eyebrow={builderCopy.eyebrow}
              title={builderCopy.title}
              description={builderCopy.description}
            />
            {!uploadData && !prep101AccessBlocked && (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
                  gap: 12,
                  marginBottom: "1.25rem",
                }}
              >
                {builderCopy.steps.map(([number, title, description]) => (
                  <StepHint
                    key={number}
                    number={number}
                    title={title}
                    description={description}
                  />
                ))}
              </div>
            )}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "1.1rem",
              }}
            >
              {prep101AccessBlocked ? (
                <div
                  style={{
                    borderRadius: 18,
                    background: "#fffbeb",
                    border: "1px solid #f59e0b",
                    color: "#92400e",
                    padding: "1rem",
                  }}
                >
                  <div style={{ fontWeight: 900, marginBottom: 6 }}>
                    You need Prep101 guide access before uploading sides.
                  </div>
                  <div style={{ lineHeight: 1.6, marginBottom: 12 }}>
                    Your account has used all available Prep101 guides. Add a
                    single guide, grab the 3-pack, or redeem a promo code here
                    before starting the builder.
                  </div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <button
                      onClick={() => (window.location.href = PREP101_STRIPE_LINKS.singleGuide)}
                      className="btn"
                      style={{
                        background: "#0f172a",
                        color: "#fff",
                        border: "none",
                      }}
                    >
                      Buy Single A La Carte — $11.99
                    </button>
                    <button
                      onClick={() => (window.location.href = PREP101_STRIPE_LINKS.threePack)}
                      className="btn btnPrimary"
                    >
                      Buy 3-Pack — $21.99
                    </button>
                  </div>
                  <PromoCodeInput onRedeemSuccess={refreshDashboardAccess} />
                </div>
              ) : (
                <FileUpload
                  onUpload={handleFileUpload}
                  onUploadStart={handleUploadStart}
                  onUploadEnd={handleUploadEnd}
                  allowMultiple={isReader101Context}
                />
              )}

              {uploadIsUsable && (
                <div
                  style={{
                    padding: 16,
                    background: "#f0fdfa",
                    borderRadius: 12,
                    border: "1px solid #2dd4bf",
                    color: "#065f46",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                      marginBottom: "0.5rem",
                    }}
                  >
                    <span>✅</span>
                    <strong>PDF uploaded successfully!</strong>
                  </div>
                  <div style={{ fontSize: "0.875rem" }}>
                    {uploadData.uploadIds
                      ? `${uploadData.uploadIds.length} file(s) ready for guide generation • ${extractedWords} words extracted`
                      : `File ready for guide generation • ${extractedWords} words extracted`}
                  </div>
                </div>
              )}

              {uploadHasFailed && (
                <div
                  style={{
                    padding: 16,
                    background: uploadCanProceedViaDirectPdf ? "#eff6ff" : "#fef2f2",
                    borderRadius: 12,
                    border: uploadCanProceedViaDirectPdf ? "1px solid #93c5fd" : "1px solid #fca5a5",
                    color: uploadCanProceedViaDirectPdf ? "#1e3a8a" : "#991b1b",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                      marginBottom: "0.5rem",
                    }}
                  >
                    <span>{uploadCanProceedViaDirectPdf ? "ℹ️" : "⚠️"}</span>
                    <strong>
                      {uploadCanProceedViaDirectPdf
                        ? "PDF accepted. We'll use our deep-reading mode during generation."
                        : "PDF accepted, but the text is difficult to read."}
                    </strong>
                  </div>
                  <div style={{ fontSize: "0.875rem" }}>
                    {uploadCanProceedViaDirectPdf
                      ? `We'll process the original PDF file during generation to ensure we catch every detail. You can proceed below.`
                      : `Extracted ${extractedWords} words. Re-upload a clearer PDF or paste the sides text directly.`}
                  </div>
                </div>
              )}

              {canShowGuideDetails && (
                <div>
                  <h3
                    style={{
                      fontSize: "1.5rem",
                      fontWeight: "bold",
                      color: "#374151",
                      marginBottom: "1rem",
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                    }}
                  >
                    {isReader101Context ? "📖 Reader Guide Details" : "🎭 Guide Details"}
                  </h3>
                  <p style={{ color: "#6b7280", marginBottom: "1.5rem" }}>
                    {isReader101Context
                      ? "Fill in the details below to generate scene-specific coaching for the reader."
                      : "Fill in the details below to generate your personalized audition guide."}
                  </p>

                  <GuideForm
                    onSubmit={handleGenerateGuide}
                    hasFile={canShowGuideDetails}
                    isSubmitting={isGenerating}
                    disabled={!canGenerate}
                    defaultMode={builderMode}
                    lockMode={isReader101Context || guideFilter === "prep101"}
                  />
                </div>
              )}

              {!uploadData && !isUploadingPdf && !prep101AccessBlocked && (
                <div
                  style={{
                    textAlign: "center",
                    padding: "2rem",
                    color: "#6b7280",
                  }}
                >
                  <p>Upload your audition sides (PDF) to get started.</p>
                </div>
              )}
            </div>
          </div>

          {/* ── GUIDE LIBRARY ─────────────────────────────────────── */}
          <div className="card-white">
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: '1rem', flexWrap: 'wrap' }}>
            <SectionHeader
              eyebrow="Saved Work"
              title="Your guides"
              description="Open, download, or filter the guides saved to this account."
            />
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {['all', 'prep101', 'reader101', 'bold_choices'].map(f => (
                <button
                  key={f}
                  onClick={() => {
                    setGuideFilter(f);
                    const nextUrl = f === 'all' ? '/dashboard' : `/dashboard?product=${encodeURIComponent(f)}`;
                    window.history.replaceState({}, '', nextUrl);
                  }}
                  style={{
                    padding: '4px 12px', borderRadius: 999, fontSize: 12, fontWeight: 700,
                    border: '1.5px solid',
                    cursor: 'pointer',
                    background: guideFilter === f ? '#0f172a' : 'transparent',
                    color: guideFilter === f ? '#fff' : '#64748b',
                    borderColor: guideFilter === f ? '#0f172a' : '#e2e8f0',
                  }}
                >
                  {f === 'all' ? 'All' : f === 'prep101' ? 'Prep101' : f === 'reader101' ? 'Reader101' : 'Bold Choices'}
                </button>
              ))}
            </div>
          </div>

          {guidesLoading ? (
            <p style={{ color: '#94a3b8', fontSize: 13 }}>Loading…</p>
          ) : guides.filter(g => guideFilter === 'all' || g.guideType === guideFilter).length === 0 ? (
            <p style={{ color: '#94a3b8', fontSize: 13, textAlign: 'center', padding: '2rem 0' }}>
              No guides yet. Generate one above!
            </p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {guides
                .filter(g => guideFilter === 'all' || g.guideType === guideFilter)
                .map(g => {
                  const typeColors = { prep101: '#f59e0b', reader101: '#14b8a6', bold_choices: '#FF4D4D' };
                  const typeLabels = { prep101: 'Prep101', reader101: 'Reader101', bold_choices: 'Bold Choices' };
                  const color = typeColors[g.guideType] || '#94a3b8';
                  return (
                    <div key={g.id} style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '10px 14px', borderRadius: 10,
                      border: '1px solid #e2e8f0', background: '#f8fafc',
                      gap: 12, flexWrap: 'wrap',
                    }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 700, color: '#0f172a', fontSize: 14, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {g.characterName} — {g.productionTitle}
                        </div>
                        <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>
                          {new Date(g.createdAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                        </div>
                      </div>
                      <span style={{
                        fontSize: 11, fontWeight: 700, padding: '2px 9px', borderRadius: 999,
                        background: color + '18', color, border: `1px solid ${color}44`,
                        whiteSpace: 'nowrap',
                      }}>
                        {typeLabels[g.guideType] || g.guideType}
                      </span>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <a
                          href={g.viewUrl || `/guide/${g.id}`}
                          style={{
                            fontSize: 12, fontWeight: 700, color: '#0f172a',
                            background: '#fff', border: '1px solid #cbd5e1', borderRadius: 8,
                            padding: '5px 12px', textDecoration: 'none', whiteSpace: 'nowrap',
                          }}
                        >
                          Open
                        </a>
                        <a
                          href={buildGuideDownloadUrl(
                            g.htmlUrl || `/api/guides/${g.id}/html`
                          )}
                          download
                          style={{
                            fontSize: 12, fontWeight: 700, color: '#0f172a',
                            background: '#e2e8f0', borderRadius: 8,
                            padding: '5px 12px', textDecoration: 'none', whiteSpace: 'nowrap',
                          }}
                        >
                          HTML
                        </a>
                        <a
                          href={buildGuideDownloadUrl(
                            g.pdfUrl || `/api/guides/${g.id}/pdf`
                          )}
                          download
                          style={{
                            fontSize: 12, fontWeight: 700, color: '#0f172a',
                            background: 'var(--gold, #f59e0b)', borderRadius: 8,
                            padding: '5px 12px', textDecoration: 'none', whiteSpace: 'nowrap',
                          }}
                        >
                          PDF
                        </a>
                      </div>
                    </div>
                  );
                })}
            </div>
          )}
          </div>
          {/* ── END GUIDE LIBRARY ─────────────────────────────────── */}

          {/* ── PLAYBOOK ───────────────────────────────────────────── */}
          <div className="card-white">
            <SectionHeader
              eyebrow="Bold Choices"
              title="Your playbook"
              description="Saved choice snippets from the ⭐ Add to Playbook button."
            />

            {playbookLoading ? (
              <p style={{ color: "#94a3b8", fontSize: 13 }}>Loading…</p>
            ) : playbookItems.length === 0 ? (
              <p style={{ color: "#94a3b8", fontSize: 13, textAlign: "center", padding: "1.5rem 0" }}>
                No saved choices yet. Save one from a Bold Choices guide.
              </p>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {playbookItems.map((item) => (
                  <div
                    key={item.id}
                    style={{
                      border: "1px solid #e2e8f0",
                      borderRadius: 12,
                      background: "#fff",
                      padding: "12px 14px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 10,
                        marginBottom: 8,
                        flexWrap: "wrap",
                      }}
                    >
                      <div style={{ fontSize: 12, fontWeight: 800, color: "#ef4444", letterSpacing: "0.06em", textTransform: "uppercase" }}>
                        Playbook Choice
                      </div>
                      <div style={{ fontSize: 12, color: "#94a3b8" }}>
                        {item.createdAt
                          ? new Date(item.createdAt).toLocaleDateString(undefined, {
                              month: "short",
                              day: "numeric",
                              year: "numeric",
                            })
                          : ""}
                      </div>
                    </div>

                    {(item.character || item.show) && (
                      <div style={{ fontSize: 13, color: "#64748b", marginBottom: 8 }}>
                        {[item.character, item.show].filter(Boolean).join(" — ")}
                      </div>
                    )}

                    <div style={{ fontSize: 14, color: "#0f172a", lineHeight: 1.6, whiteSpace: "pre-wrap" }}>
                      {item.choice}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          {/* ── END PLAYBOOK ───────────────────────────────────────── */}
        </div>

        {/* Footer */}
        <Footer />
      </div>
    </>
  );
};

export default Dashboard;
